import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { useState } from "react";
import moment from "moment";
import { notificationsState } from "src/store/notifications";
import { useRecoilState } from "recoil";
import { useExportKafdReport } from "src/api/sales";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LoadingButton } from "@mui/lab";
import { downloadFile } from "src/utils";
import { DesktopTimePicker } from "@mui/x-date-pickers";

const initStartDate = moment().startOf("month").hour(8).toDate();
const initEndDate = moment().startOf("day").add(1, "days").hour(8).toDate();

const initFromTime = moment().startOf("day").hour(16).toDate();
const initToTime = moment().startOf("day").hour(1).toDate();

const KAFDReportPage = () => {
  const [_, setNotifications] = useRecoilState(notificationsState);
  const [startDate, setStartDate] = useState<Date | null>(initStartDate);
  const [endDate, setEndDate] = useState<Date | null>(initEndDate);
  const [fromTime, setFromTime] = useState<Date | null>(initFromTime);
  const [toTime, setToTime] = useState<Date | null>(initToTime);
  const [openStartDate, setOpenStartDate] = useState<boolean>(false);
  const [openEndDate, setOpenEndDate] = useState<boolean>(false);
  const [openFromTime, setOpenFromTime] = useState<boolean>(false);
  const [openToTime, setOpenToTime] = useState<boolean>(false);
  const [type, setType] = useState<"daily" | "monthly">("monthly");

  const minDate = new Date("2024-01-01T00:00:00");
  const maxDate = moment().add(1, "year").endOf("year").toDate();

  const filters = {
    ...(startDate && {
      startDate:
        type === "monthly"
          ? moment(startDate).startOf("day").add(8, "hours").valueOf()
          : moment(startDate)
              .set({
                hour: moment(fromTime).hour(),
                minute: moment(fromTime).minute(),
                second: moment(fromTime).second(),
              })
              .valueOf(),
    }),
    ...(endDate && {
      endDate:
        type === "monthly"
          ? moment(endDate).startOf("day").add(8, "hours").valueOf()
          : moment(endDate)
              .set({
                hour: moment(toTime).hour(),
                minute: moment(toTime).minute(),
                second: moment(toTime).second(),
              })
              .valueOf(),
    }),
  };

  // APIS
  const { isFetching, refetch } = useExportKafdReport(filters);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setType((event.target as HTMLInputElement).value as "daily" | "monthly");
  };

  const handleExport = () => {
    if (startDate && endDate && startDate > endDate) {
      setNotifications((prev) => [
        ...prev,
        {
          type: "error",
          message: "Start date must be before end date",
        },
      ]);
      return;
    }
    const maxRange = 31 * 24 * 60 * 60 * 1000;

    if (
      startDate &&
      endDate &&
      endDate.getTime() - startDate.getTime() > maxRange
    ) {
      setNotifications((prev) => [
        ...prev,
        {
          type: "error",
          message: "Max date range is 31 days",
        },
      ]);
      return;
    }

    refetch().then((res) => {
      if (res.status === "success" && res.data) {
        const utf8CSVData = `\uFEFF${res.data}`;
        downloadFile(utf8CSVData, "text/csv", "KAFD-report");
        setNotifications((prev) => [
          ...prev,
          {
            type: "success",
            message: "Downloaded successfully",
          },
        ]);
      }
    });
  };

  console.log({ startDate, fromTime });

  return (
    <Box p={{ xs: "20px 12px", md: "30px 20px", lg: "30px 30px" }}>
      <Typography fontWeight={600} fontSize={"22px"} mb={{ xs: 2, md: 4 }}>
        KAFD API Report
      </Typography>

      {/* filters */}
      <Stack direction={{ xs: "column", md: "row" }} gap={"10px"} mb={3}>
        <Stack direction={"row"} gap={"10px"} flexWrap={"wrap"} flexGrow={1}>
          <Box>
            <FormControl>
              <RadioGroup
                aria-labelledby="demo-controlled-radio-buttons-group"
                name="controlled-radio-buttons-group"
                value={type}
                onChange={handleChange}
                row
              >
                <FormControlLabel
                  value="monthly"
                  control={<Radio />}
                  label="Monthly"
                />
                <FormControlLabel
                  value="daily"
                  control={<Radio />}
                  label="Daily"
                />
              </RadioGroup>
            </FormControl>
            <Stack direction={{ xs: "column", md: "row" }} mt={2} gap={"10px"}>
              {/* start date */}
              <DatePicker
                label="Start Date"
                slotProps={{
                  textField: {
                    size: "small",
                    onClick: () => setOpenStartDate(true),
                    sx: { width: "155px" },
                    inputProps: { readOnly: true },
                  },
                }}
                format={"dd/MM/yyyy"}
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                monthsPerRow={4}
                name="start date"
                disableOpenPicker
                open={openStartDate}
                onClose={() => setOpenStartDate(false)}
                minDate={minDate}
                maxDate={maxDate}
              />
              {/* end date */}
              <DatePicker
                label="End Date"
                slotProps={{
                  textField: {
                    size: "small",
                    onClick: () => setOpenEndDate(true),
                    sx: { width: "155px" },
                    inputProps: { readOnly: true },
                  },
                }}
                format={"dd/MM/yyyy"}
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                name="end date"
                disableOpenPicker
                open={openEndDate}
                onClose={() => setOpenEndDate(false)}
                minDate={minDate}
                maxDate={maxDate}
              />
              {type === "daily" ? (
                <>
                  <DesktopTimePicker
                    value={fromTime}
                    onChange={(newValue) => setFromTime(newValue)}
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: { width: "135px" },
                        inputProps: { readOnly: true },
                        onClick: () => setOpenFromTime(true),
                      },
                    }}
                    label="From"
                    ampm={true}
                    format={"hh:mm aa"}
                    open={openFromTime}
                    onOpen={() => setOpenFromTime(true)}
                    onClose={() => setOpenFromTime(false)}
                  />
                  <DesktopTimePicker
                    value={toTime}
                    onChange={(newValue) => setToTime(newValue)}
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: { width: "135px" },
                        inputProps: { readOnly: true },
                        onClick: () => setOpenToTime(true),
                      },
                    }}
                    label="To"
                    ampm={true}
                    format={"hh:mm aa"}
                    open={openToTime}
                    onOpen={() => setOpenToTime(true)}
                    onClose={() => setOpenToTime(false)}
                  />
                </>
              ) : null}
            </Stack>
          </Box>
        </Stack>
        <Stack direction={"row"}>
          <LoadingButton
            loading={isFetching}
            variant="contained"
            onClick={handleExport}
            sx={{ width: "100px" }}
          >
            Export
          </LoadingButton>
        </Stack>
      </Stack>
    </Box>
  );
};

export default KAFDReportPage;
